import { useMemo } from 'react';
import { DropzoneOptions } from 'react-dropzone';
import { PermissionAction, PermissionResource } from '@sib/api';
import { useCan } from '@sib/auth';
import { FileUploadStore } from '@sib/files';
import { useTranslation } from '@sib/i18n';
import { Button, Card, Checkbox, cn, Tooltip } from '@sib/ui';
import { ClosedEye, SortArrow, Trash } from '@sib/ui/icons';
import { getFileCounts } from '@sib/utils';
import { FileUploadItem } from '../FileManager/FileUploadItem/FileUploadItem';
import { useUploadingFilesList } from './UploadingFilesList.utils';

interface UploadingFilesListProps {
  fileUploadStore: FileUploadStore;
  fileListClassName?: string;
  dropzoneConfig?: Partial<DropzoneOptions>;
  hideSensitiveActions?: boolean;
}

export const UploadingFilesList = ({
  fileListClassName,
  fileUploadStore,
  dropzoneConfig,
  hideSensitiveActions = false,
}: UploadingFilesListProps) => {
  const { t } = useTranslation();
  const can = useCan();
  const canUserMarkAsSensitive = useMemo(
    () =>
      can({
        permissions: {
          actions: PermissionAction.CREATE,
          resources: PermissionResource.SensitiveDocuments,
        },
      }),
    [can]
  );

  const {
    sortedFiles,
    areFilesUploading,
    areThereAnyFiles,
    allSelected,
    anySelected,
    files,
    removeFile,
    toggleSortOrder,
    updateFile,
    toggleSelectAllFiles,
  } = useUploadingFilesList({ fileUploadStore, dropzoneConfig });

  const { deleteSelectedFiles, markSelectedFilesAsSensitive } = fileUploadStore;
  const areAllFilesSensitive = useMemo(
    () => files.every(file => file.isSensitive),
    [files]
  );

  const areActionsDisabled = !areThereAnyFiles || !anySelected;

  return (
    <Card className="h-full flex-1 overflow-hidden p-3 sm:p-[22px]">
      <div className="flex h-full flex-col space-y-4">
        <div className="mb-4 flex items-center justify-between pl-4">
          <div className="flex items-center gap-2">
            <Checkbox
              checked={allSelected}
              onCheckedChange={toggleSelectAllFiles}
              disabled={areFilesUploading || !areThereAnyFiles}
            />
            <Button
              variant="ghost"
              type="button"
              className="text-muted-foreground px-2 sm:p-4"
              onClick={toggleSelectAllFiles}
            >
              {t('selectAll')}
            </Button>
            <Button
              variant="ghost"
              type="button"
              className="px-2.5 sm:p-4"
              onClick={toggleSortOrder}
              disabled={files.length === 0}
            >
              <SortArrow className="text-primary size-5" />
            </Button>
          </div>
          <div className="flex items-center gap-5">
            <p className="text-muted-foreground text-sm">
              {t('fileManager.transfer')} <br />
              {getFileCounts(files)} {t('fileManager.files')}
            </p>
            <Button
              disabled={areActionsDisabled}
              variant="outline"
              onClick={deleteSelectedFiles}
              className="disabled:text-primary/50"
              type="button"
            >
              <Trash
                className={cn(
                  'text-primary size-5',
                  areActionsDisabled && 'text-muted-foreground opacity-60'
                )}
              />
              <span className="hidden sm:block"> {t('delete')}</span>
            </Button>
            {canUserMarkAsSensitive && !hideSensitiveActions && (
              <Tooltip
                content={t('fileManager.markAsSensitive.multiMarkTooltip')}
                TooltipTriggerProps={{ asChild: true }}
                TooltipContentProps={{
                  variant: 'secondary',
                  className: 'text-sm max-w-[320px] p-2.5',
                  align: 'end',
                }}
              >
                <Button
                  disabled={areActionsDisabled || areAllFilesSensitive}
                  variant="outline"
                  className="disabled:text-primary/50"
                  onClick={markSelectedFilesAsSensitive}
                  type="button"
                >
                  <ClosedEye
                    className={cn(
                      'text-primary size-5',
                      (areActionsDisabled || areAllFilesSensitive) &&
                        'text-muted-foreground opacity-60'
                    )}
                  />
                  <span className="hidden sm:block">
                    {t('fileManager.markAsSensitive.title')}
                  </span>
                </Button>
              </Tooltip>
            )}
          </div>
        </div>
        {files.length > 0 && (
          <div
            className={cn(
              'flex-1 space-y-4 overflow-y-auto',
              fileListClassName
            )}
          >
            {sortedFiles.map(fileItem => (
              <FileUploadItem
                key={fileItem.id}
                fileItem={fileItem}
                onUpdate={updateFile}
                onRemove={removeFile}
                loading={areFilesUploading}
                canUserMarkAsSensitive={
                  canUserMarkAsSensitive && !hideSensitiveActions
                }
              />
            ))}
          </div>
        )}
      </div>
    </Card>
  );
};
